/**
 * CPCatalogHorizon.tsx
 *
 * W66.C — Methodology Workspace — Horizon 1: CP Catalog
 *
 * Displays all 10 M-PACT checkpoints in a card grid.
 * Each card shows: CP number, em-dash label, weight, brief description,
 * doctrine note, and disqualifier indicator.
 *
 * Weights locked: CP1=5, CP2=20, CP3=10, CP4=10, CP5=15, CP6=15,
 *                 CP7=15, CP8=5, CP9=3, CP10=2. Sum=100.
 *
 * CANON: No exclamation points. No emojis. M-PACT hyphenated.
 * Em-dash format CP{N} — {Name}. "Checkpoint" not "Gate".
 * Banned strings enforced at data layer (cp-catalog.ts).
 */

import { CP_CATALOG, type CpCatalogEntry } from "@/lib/cp-catalog";

// ─── Weight bar ───────────────────────────────────────────────────────────────

function WeightBar({ weight }: { weight: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
      <div
        style={{
          flex: 1,
          height: 4,
          background: "#EFEFEE",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${weight}%`,
            background: "#DC2626",
            borderRadius: 2,
          }}
        />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#0B0B0F", minWidth: 32, textAlign: "right" }}>
        {weight}%
      </span>
    </div>
  );
}

// ─── Single CP card ───────────────────────────────────────────────────────────

function CpCard({ cp }: { cp: CpCatalogEntry }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Disqualifier badge */}
      {cp.isDisqualifier && (
        <div
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            background: "rgba(220,38,38,0.10)",
            color: "#DC2626",
            border: "1px solid rgba(220,38,38,0.35)",
            borderRadius: 9999,
            padding: "2px 7px",
          }}
        >
          Disqualifier
        </div>
      )}

      {/* CP number badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "#0B0B0F",
            color: "#FAFAF7",
            fontSize: 12,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          {cp.number}
        </span>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0B0B0F", lineHeight: 1.2 }}>
          CP{cp.number} — {cp.name}
        </div>
      </div>

      {/* Description */}
      <p style={{ fontSize: 12, color: "#3D3D42", lineHeight: 1.6, margin: "0 0 10px" }}>
        {cp.description}
      </p>

      {/* Doctrine note */}
      <div
        style={{
          background: "#FAFAF7",
          border: "1px solid #EFEFEE",
          borderRadius: 6,
          padding: "8px 12px",
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#6B6B70", textTransform: "uppercase", marginBottom: 4 }}>
          Key question
        </div>
        <p style={{ fontSize: 11, color: "#6B6B70", lineHeight: 1.55, margin: 0 }}>
          {cp.doctrineNote}
        </p>
      </div>

      {/* Weight bar */}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#6B6B70", textTransform: "uppercase", marginBottom: 4 }}>
        Checkpoint weight
      </div>
      <WeightBar weight={cp.weight} />
    </div>
  );
}

// ─── Horizon component ────────────────────────────────────────────────────────

export default function CPCatalogHorizon() {
  return (
    <section>
      {/* Summary strip */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        <div
          style={{
            background: "#0B0B0F",
            borderRadius: 8,
            padding: "12px 18px",
            color: "#FAFAF7",
            minWidth: 140,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>10</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>Checkpoints</div>
        </div>
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 8,
            padding: "12px 18px",
            minWidth: 140,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 800, color: "#DC2626", letterSpacing: "-0.02em" }}>4</div>
          <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 2 }}>Disqualifier checkpoints</div>
        </div>
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 8,
            padding: "12px 18px",
            minWidth: 140,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 800, color: "#0B0B0F", letterSpacing: "-0.02em" }}>100</div>
          <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 2 }}>Total weight points</div>
        </div>
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 8,
            padding: "12px 18px",
            minWidth: 160,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F", letterSpacing: "-0.01em" }}>M-PACT</div>
          <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 2 }}>Mi6 qualification framework</div>
        </div>
      </div>

      {/* CP grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 14,
        }}
      >
        {CP_CATALOG.map((cp) => (
          <CpCard key={cp.number} cp={cp} />
        ))}
      </div>

      {/* Weight legend */}
      <div
        style={{
          marginTop: 20,
          padding: "12px 16px",
          background: "#FAFAF7",
          border: "1px solid #E5E5E7",
          borderRadius: 8,
          fontSize: 12,
          color: "#6B6B70",
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: "#0B0B0F" }}>Weight distribution — </strong>
        Weights are locked and sum to 100. CP2 (Need / Pain / Demonstration, 20%) carries the highest single weight because a misdiagnosis at that stage collapses every downstream checkpoint. CP1, CP2, CP5 are disqualifier checkpoints — a deal cannot advance past them without satisfying the core question.
      </div>
    </section>
  );
}
